import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActiveCommentTypeEnum } from 'src/app/types/active-comment-type.enum';
import { ActiveCommentInterface } from 'src/app/types/active-comment.interface';
import { CommentInterface } from 'src/app/types/comment.interface';
import { LOCALE_ID } from '@angular/core';
import { UserService } from '../../../user.service';
import { User } from 'src/app/types/user.interface';

@Component({
  selector: 'app-comment',
  templateUrl: './comment.component.html',
  styleUrls: ['./comment.component.scss'],
  providers: [{provide: LOCALE_ID, useValue: 'fr' }],
})
export class CommentComponent implements OnInit {

  @Input() comment!: CommentInterface;
  @Input() activeComment!: ActiveCommentInterface | null;
  //@Input() replies!: CommentInterface[];
  @Input() articleId!: string;
  @Input() commentType!: string;
  @Input() parentId: string | null = null;
  @Input() liked!: boolean;

  @Output()
  setActiveComment = new EventEmitter<ActiveCommentInterface | null>();
  @Output()
  deleteComment = new EventEmitter<string>();
  @Output()
  addComment = new EventEmitter<{ text: string; parentId: string | null }>();
  @Output()
  updateComment = new EventEmitter<{ text: string; commentId: string }>();
  @Output()
  setLike = new EventEmitter<{ commentId: string; like: boolean }>();


  private _replies: CommentInterface[] = [];

@Input()
  set replies(value: CommentInterface[]) {
    this._replies = value;
    this.refreshReplies();
  }
  get replies(): CommentInterface[] {
    return this._replies;
  }


  createdAt: string = '';
  commentLiked: boolean = false;
  canReply: boolean = false;
  canEdit: boolean = false;
  canLike: boolean = true;
  canDelete: boolean = false;
  activeCommentType = ActiveCommentTypeEnum;
  replyId: string | null = null;
  showReplies: boolean = false;

  displayedReplies: CommentInterface[] = [];
  showAllReplies: boolean = false;
  maxRepliesToShow: number = 2;

  constructor(private user: UserService) {}

  ngOnInit(): void {
    const fiveMinutes = 300;
    let comment_date = new Date(this.comment.attributes.createdAt);
    let current_date = new Date();
    let seconds = (current_date.getTime() - comment_date.getTime()) / 1000;
    const timePassed = seconds > fiveMinutes;

    const commentUserId = this.comment?.attributes?.user?.data?.id;
    const currentUserId = this.user.getUserId();
  
    this.createdAt = new Date(this.comment.attributes.createdAt).toLocaleDateString();
    this.canReply = Boolean(this.user.getUser() && this.user.getUserId() && !this.getCommentParent()); // ✅ seulement si ce n'est pas une réponse;
    this.canLike = Boolean(this.user.getUser() && this.user.getUserId());
    
    this.canEdit = currentUserId && commentUserId && currentUserId === commentUserId && !timePassed;
    this.canDelete = currentUserId && commentUserId && currentUserId === commentUserId && this.replies.length === 0 && !timePassed;

    this.replyId = this.parentId ? this.parentId : this.comment.id;
  
    // 🔽 Trie les réponses par date décroissante (plus récentes en premier)
    this.replies = this.replies.sort((a, b) =>
      new Date(b.attributes.publishedAt).getTime() - new Date(a.attributes.publishedAt).getTime()
    );
  
    this.updateDisplayedReplies();

    // Get users who liked the comment
    const likedByUsers = this.comment.attributes.liked_by?.data || [];
  
    this.comment.liked = likedByUsers.some((u: any) => u.id === currentUserId);
    //this.canLike = Boolean(currentUserId);

  }

  ngOnChanges(): void {
    this.refreshReplies();
  }

  refreshReplies(): void {
    if (this._replies) {
      this._replies = this._replies.sort((a, b) =>
        new Date(b.attributes.publishedAt).getTime() - new Date(a.attributes.publishedAt).getTime()
      );
  
      this.updateDisplayedReplies();
    }
  }
  
  
  getCommentUser(): any {
      return this.comment?.attributes.user?.data;
  }

  getCommentParent(): string | null {
    return this.comment.attributes.parentId;
  }
  

  // This method is used to set the active comment for replying or editing
  updateDisplayedReplies(): void {
    if (this.replies) {
      this.displayedReplies = this.showAllReplies
        ? [...this.replies]
        : this.replies.slice(0, this.maxRepliesToShow);
    }
  }  

  toggleReplies(): void {
    this.showReplies = !this.showReplies;
  }

  isLiked(commentId: string) {
    this.commentLiked = (this.liked && this.comment.id == commentId) || false; // TODO:: tenir en compte lors des prochaines connections lorsque l'utilisateur a liké
  }

  isReplying(): boolean {
    if (!this.activeComment) {
      return false;
    }
    return (
      this.activeComment.id === this.comment.id &&
      this.activeComment.type === this.activeCommentType.replying
    );
  }

  isEditing(): boolean {
    if (!this.activeComment) {
      return false;
    }
    return (
      this.activeComment.id === this.comment.id &&
      this.activeComment.type === 'editing'
    );
  }

  isLiking(): boolean {
    if (!this.activeComment) {
      return false;
    }
    return (
      this.activeComment.id === this.comment.id &&
      this.activeComment.type === 'liking'
    );
  }

  getUser(): User {
    return this.comment.attributes.user;
  }

  getUserName(): string {
    const user = this.comment.attributes.user;
  
    if (user?.data?.attributes?.name) {
      return user.data.attributes.name;
    }
  
    return 'anonyme';
  }  

  isToday(date: string): boolean {
    const today = new Date();
    const inputDate = new Date(date);
    return inputDate.toDateString() === today.toDateString();
  }

  showMoreReplies() {
    this.showAllReplies = true;
    this.displayedReplies = this.replies;
  }  

  handleReplySubmit(text: string): void {
    this.addComment.emit({ text, parentId: this.replyId });
    this.setActiveComment.emit(null);
    this.showReplies = true;
  }  

  toggleLike(): void {
    const userId = this.user.getUserId();
    if (!userId) return;
  
    const alreadyLiked = this.comment.liked;
    this.comment.liked = !alreadyLiked;
  
    if (alreadyLiked) {
      this.comment.attributes.likes -= 1;
    } else {
      this.comment.attributes.likes += 1;
    }
  
    this.setLike.emit({ commentId: this.comment.id, like: !alreadyLiked });
  }  
  
}
